/**
 * Product Optimized Form Custom Element
 * Handles variant selection, quantity controls, and form interactions
 * for the product-optimized section
 *
 * This is isolated from shared JS to avoid hardcoded logic from other templates
 */
class ProductOptimizedForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.productId = null;
    this.variantIdInput = null;
    this.variantSelect = null;
    this.quantityInput = null;
    this.variants = [];
    this.currentVariant = null;
    this.productData = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Find the product form
    this.form = this.querySelector('.product-form');
    if (!this.form) {
      return;
    }

    // Mark as optimized to prevent other scripts from initializing
    this.form.setAttribute('data-optimized-form', 'true');
    this.setAttribute('data-optimized-initialized', 'true');

    // Get product ID from form data attribute
    this.productId = this.form.dataset.productId;
    if (!this.productId) {
      console.warn('ProductOptimizedForm: No product ID found');
      return;
    }

    // Get product data and variants
    this.loadProductData();

    // Get form elements
    this.variantIdInput = this.form.querySelector('input[name="id"].product-variant-id');
    this.variantSelect = this.form.querySelector('#product-select');
    this.quantityInput = this.form.querySelector('.quantity-input');

    // Setup functionality
    this.setupQuantityControls();
    this.setupVariantSelection();
    this.setupFormSubmission();

    // Set initial variant
    this.setInitialVariant();
  }

  /**
   * Load product data from window or data attributes
   */
  loadProductData() {
    // Try to get from window object first (set by Liquid)
    if (window.productData && window.productData[this.productId]) {
      this.productData = window.productData[this.productId];
      this.variants = this.productData.variants || [];
    }
    // Try to get from hidden JSON script tag
    else {
      const productJsonEl = document.querySelector(`#product-json-${this.productId}`);
      if (productJsonEl) {
        try {
          this.productData = JSON.parse(productJsonEl.textContent);
          this.variants = this.productData.variants || [];
        } catch (e) {
          console.error('ProductOptimizedForm: Failed to parse product JSON', e);
        }
      }
    }

    // If still no variants, try to extract from variant select options
    if (this.variants.length === 0 && this.variantSelect) {
      this.variants = this.extractVariantsFromSelect();
    }

    if (this.variants.length === 0) {
      console.warn('ProductOptimizedForm: No variants found for product', this.productId);
    }
  }

  /**
   * Extract variant data from the variant select element
   */
  extractVariantsFromSelect() {
    if (!this.variantSelect) return [];

    const variants = [];
    const options = this.variantSelect.querySelectorAll('option');

    options.forEach(option => {
      const variantId = option.value;
      const variantTitle = option.textContent;

      // Basic variant structure - will be enriched by variant change events
      variants.push({
        id: parseInt(variantId),
        title: variantTitle,
        available: true,
        options: variantTitle.split(' / '),
        option1: variantTitle.split(' / ')[0] || null,
        option2: variantTitle.split(' / ')[1] || null,
        option3: variantTitle.split(' / ')[2] || null
      });
    });

    return variants;
  }

  /**
   * Set up quantity +/- controls
   */
  setupQuantityControls() {
    const minusBtn = this.form.querySelector('.qtyminus');
    const plusBtn = this.form.querySelector('.qtyplus');

    if (!minusBtn || !plusBtn || !this.quantityInput) return;

    // Mark buttons to prevent duplicate handlers
    minusBtn.setAttribute('data-optimized-quantity', 'true');
    plusBtn.setAttribute('data-optimized-quantity', 'true');

    // Use capture phase to intercept before other handlers
    minusBtn.addEventListener('click', (e) => this.handleQuantityChange(e, -1), true);
    plusBtn.addEventListener('click', (e) => this.handleQuantityChange(e, 1), true);
  }

  /**
   * Handle quantity change from +/- buttons
   */
  handleQuantityChange(e, delta) {
    e.preventDefault();
    e.stopImmediatePropagation(); // Prevent other handlers from firing

    const currentVal = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || 9999;
    const step = parseInt(this.quantityInput.step) || 1;

    let newVal = currentVal + (delta * step);

    if (newVal >= min && newVal <= max) {
      this.quantityInput.value = newVal;
    }
  }

  /**
   * Set up variant selection listeners
   */
  setupVariantSelection() {
    // Listen to swatch radio changes
    const swatchInputs = this.form.querySelectorAll('.swatch input[type="radio"]');
    swatchInputs.forEach(input => {
      input.addEventListener('change', () => this.updateVariantFromSwatches());
    });

    // Listen to variant select dropdown changes (if present)
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => this.updateVariantFromSelect());
    }

    // Listen for external variant change events (e.g., from gallery or other components)
    document.addEventListener('variant:selected', (event) => {
      if (event.detail && event.detail.productId === this.productId) {
        this.handleExternalVariantChange(event.detail.variant);
      }
    });
  }

  /**
   * Set initial variant based on form state
   */
  setInitialVariant() {
    // First, check if there's a variant parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');
    
    if (variantIdFromUrl) {
      const variantId = parseInt(variantIdFromUrl);
      const variant = this.variants.find(v => v.id === variantId);
      if (variant) {
        this.currentVariant = variant;
        // Update form inputs to match URL variant
        if (this.variantIdInput) {
          this.variantIdInput.value = variant.id;
        }
        if (this.variantSelect) {
          this.variantSelect.value = variant.id;
        }
        this.updateSwatchesForVariant(variant);
        this.dispatchVariantChangeEvent(variant);
        return;
      }
    }

    // If no URL variant, check form state and update URL
    if (this.variantIdInput && this.variantIdInput.value) {
      const variantId = parseInt(this.variantIdInput.value);
      const variant = this.variants.find(v => v.id === variantId);
      if (variant) {
        this.currentVariant = variant;
        this.updateURL(variant);
        this.dispatchVariantChangeEvent(variant);
        return;
      }
    }
    
    // Try to get from swatches and update URL
    this.updateVariantFromSwatches();
  }

  /**
   * Update variant based on selected swatches
   */
  updateVariantFromSwatches() {
    const selectedOptions = this.getSelectedOptionsFromSwatches();

    if (selectedOptions.length === 0) return;

    // Find matching variant
    const matchingVariant = this.findVariantByOptions(selectedOptions);

    if (matchingVariant) {
      this.setCurrentVariant(matchingVariant);
    } else {
      console.warn('ProductOptimizedForm: No variant found for options', selectedOptions);
    }
  }

  /**
   * Update variant based on variant select dropdown
   */
  updateVariantFromSelect() {
    if (!this.variantSelect) return;

    const variantId = parseInt(this.variantSelect.value);
    const variant = this.variants.find(v => v.id === variantId);

    if (variant) {
      this.setCurrentVariant(variant);
      this.updateSwatchesForVariant(variant);
    }
  }

  /**
   * Get selected options from swatch radio buttons
   */
  getSelectedOptionsFromSwatches() {
    const selectedOptions = [];
    const swatches = this.form.querySelectorAll('.swatch');

    swatches.forEach(swatch => {
      const optionIndex = swatch.dataset.optionIndex;
      const checkedInput = swatch.querySelector('input[type="radio"]:checked');

      if (checkedInput) {
        selectedOptions[optionIndex] = checkedInput.value;
      }
    });

    return selectedOptions;
  }

  /**
   * Find variant that matches the selected options
   */
  findVariantByOptions(selectedOptions) {
    return this.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        if (!option) return true; // Skip empty options
        const variantOption = variant[`option${index + 1}`];
        return variantOption === option;
      });
    });
  }

  /**
   * Update swatch selections to match variant
   */
  updateSwatchesForVariant(variant) {
    const swatches = this.form.querySelectorAll('.swatch');

    swatches.forEach(swatch => {
      const optionIndex = parseInt(swatch.dataset.optionIndex);
      const variantOption = variant[`option${optionIndex + 1}`];

      if (variantOption) {
        const matchingInput = swatch.querySelector(`input[value="${variantOption}"]`);
        if (matchingInput && !matchingInput.checked) {
          matchingInput.checked = true;
        }
      }
    });
  }

  /**
   * Set the current variant and update form state
   */
  setCurrentVariant(variant) {
    this.currentVariant = variant;

    // Update hidden variant ID input
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    // Update variant select dropdown
    if (this.variantSelect && this.variantSelect.value !== variant.id.toString()) {
      this.variantSelect.value = variant.id;
    }

    // Update quantity rules if available
    if (this.quantityInput && variant.quantity_rule) {
      this.quantityInput.min = variant.quantity_rule.min || 1;
      this.quantityInput.max = variant.quantity_rule.max || 9999;
      this.quantityInput.step = variant.quantity_rule.increment || 1;
    }

    // Update availability state
    this.updateAvailabilityState(variant);

    // Update URL with variant parameter
    this.updateURL(variant);

    // Dispatch variant change event for other components (e.g., subscription handler)
    this.dispatchVariantChangeEvent(variant);
  }

  /**
   * Handle variant change from external source
   */
  handleExternalVariantChange(variant) {
    this.setCurrentVariant(variant);
    this.updateSwatchesForVariant(variant);
  }

  /**
   * Update form availability state based on variant
   */
  updateAvailabilityState(variant) {
    const addToCartBtn = this.form.querySelector('.addtocart button[type="submit"]');
    const soldOutSection = this.form.querySelector('.sold-out-button');

    if (variant.available) {
      if (addToCartBtn) addToCartBtn.disabled = false;
      if (soldOutSection) soldOutSection.style.display = 'none';
    } else {
      if (addToCartBtn) addToCartBtn.disabled = true;
      if (soldOutSection) soldOutSection.style.display = 'block';
    }
  }

  /**
   * Update the URL with the variant parameter
   */
  updateURL(variant) {
    if (!variant || !variant.id) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);

    // Use replaceState to update URL without adding to browser history
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }

  /**
   * Dispatch custom event when variant changes
   */
  dispatchVariantChangeEvent(variant) {
    const event = new CustomEvent('variant:change', {
      detail: {
        variant: variant,
        productId: this.productId
      },
      bubbles: true,
      composed: true
    });

    this.dispatchEvent(event);
    document.dispatchEvent(event);
  }

  /**
   * Set up form submission handling
   */
  setupFormSubmission() {
    this.form.addEventListener('submit', (e) => {
      // Allow form to submit normally, but ensure we have a valid variant
      if (!this.variantIdInput || !this.variantIdInput.value) {
        e.preventDefault();
        console.error('ProductOptimizedForm: No variant selected');
        return false;
      }
    });
  }

  /**
   * Cleanup event listeners
   */
  cleanup() {
    // Event listeners will be automatically cleaned up when element is removed
  }
}

// Register the custom element
if (!customElements.get('product-optimized-form')) {
  customElements.define('product-optimized-form', ProductOptimizedForm);
}
